import { Injectable } from '@angular/core';
import { environment } from '../../environments';
import { UserData } from '../interfaces/user';
import Cookies from '../utils/cookies';
import { createIdentityClient } from './identity-client';
import { IdentitySession, signInNavigation, signInUrl } from './identity-session';
import { publishSessionContext } from './session-context';

const identityClient = createIdentityClient(environment.IDENTITY_URL)

// Set before the redirect to identity. sessionStorage so the marker dies
// with the tab: a later visit, or another window, must still reach identity.
const SIGN_IN_PENDING_KEY = 'identity_sign_in_pending'

function storageGet(store: Storage, key: string): string | null {
  try {
    return store.getItem(key)
  } catch {
    return null
  }
}

function storageRemove(store: Storage, key: string): void {
  try {
    store.removeItem(key)
  } catch {
    // Private mode and some webviews throw on storage. The redirect still proceeds.
  }
}

/** True only when this tab already left for identity and came back. */
function pendingSignInThisTab(): boolean {
  return storageGet(sessionStorage, SIGN_IN_PENDING_KEY) !== null
}

function hasPendingSignIn(): boolean {
  return pendingSignInThisTab() || storageGet(localStorage, SIGN_IN_PENDING_KEY) !== null
}

function markPendingSignIn(): void {
  storageRemove(localStorage, SIGN_IN_PENDING_KEY)
  try {
    sessionStorage.setItem(SIGN_IN_PENDING_KEY, '1')
  } catch {
    // Without the marker a same-tab bounce cannot be told from a first visit.
  }
}

function clearPendingSignIn(): void {
  storageRemove(sessionStorage, SIGN_IN_PENDING_KEY)
  storageRemove(localStorage, SIGN_IN_PENDING_KEY)
}

// Several AuthService instances and concurrent 401s can call login() in the
// same page load; only the first one navigates. A real return from identity
// is a fresh page load, which resets this.
let signInNavigationStarted = false

// One session per page. Several components provide their own AuthService,
// so the token cannot live on the instance.
export const identitySession = new IdentitySession(() => identityClient.token())

/**
 * The API no longer reads the Blizzard Authentication cookie, but requests
 * still go out with credentials, so stale copies are removed wherever the old
 * app could have written them.
 */
export function clearLegacyAuthCookie(): void {
  const domains = new Set<string | undefined>([undefined, environment.BASE_COOKIE || undefined, '.lobbyze.com'])
  domains.forEach((domain) => Cookies.remove('Authentication', domain ? { domain } : undefined))
}

clearLegacyAuthCookie()

/** APP_INITIALIZER: load the token before guards and components read the user. */
export function loadIdentitySession(): () => Promise<void> {
  return async () => {
    await identitySession.accessToken()
    const user = identitySession.currentUser
    if (user) {
      publishSessionContext({ userId: user.id, loggedIn: true })
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  login() {
    if (signInNavigationStarted) {
      return
    }
    signInNavigationStarted = true
    Cookies.set('grind_open', 'false')
    clearLegacyAuthCookie()
    const navigation = signInNavigation({
      pendingThisTab: pendingSignInThisTab(),
      identityUnreachable: identitySession.identityUnreachable,
      signOutFailed: false,
    })
    identitySession.clear()
    if (navigation === 'session-error') {
      clearPendingSignIn()
      location.href = '/session-error'
      return
    }
    if (navigation === 'sign-out-then-sign-in') {
      // Identity would bounce a live session straight back to this page.
      // Drop it so /sign-in shows the form.
      void identityClient.signOut().then(
        () => this.redirectToSignIn(),
        (err) => {
          console.error('[AUTH] identity sign-out before sign-in failed', err)
          clearPendingSignIn()
          location.href = '/session-error'
        },
      )
      return
    }
    this.redirectToSignIn()
  }

  private redirectToSignIn(): void {
    markPendingSignIn()
    location.href = signInUrl(environment.IDENTITY_URL, location.href)
  }

  async logout() {
    try {
      await identityClient.signOut()
    } catch (err) {
      console.error('[AUTH] identity sign-out failed', err)
    }
    identitySession.clear()
    clearLegacyAuthCookie()
    Cookies.set('grind_open', 'false')
    clearPendingSignIn()
    localStorage.removeItem('sites-true');
    localStorage.removeItem('sites-false');
    publishSessionContext({ userId: null, tierId: null, loggedIn: false })
    location.href = '/'
  }

  /** True once after the user returns from identity sign-in. */
  consumeSignIn(): boolean {
    if (!this.isLoggedIn() || !hasPendingSignIn()) {
      return false
    }
    clearPendingSignIn()
    return true
  }

  isLoggedIn(): boolean {
    return !!identitySession.currentUser?.email
  }

  get user(): UserData | null {
    return identitySession.currentUser
  }

  get token(): string {
    return identitySession.currentToken || ''
  }

  accessToken(force = false): Promise<string | null> {
    return identitySession.accessToken(force)
  }

  get identityUnreachable(): boolean {
    return identitySession.identityUnreachable
  }
}
